import { Language } from '../types';

export const dataPlatformTranslations: Record<Language, Record<string, string>> = {
  en: {
    // Data Platform Page
    dataPlatform: "Tailored Organisational Data Platform",
    buildFoundation: "Building the Foundation for Data-Driven Success",
    platformDesc: "We help organizations navigate the transition to data-driven operations through a structured, proven methodology.",
    
    challengeOpportunity: "The Challenge and Opportunity",
    businessContext: "In today's business landscape, data-driven decision making is no longer optional—it's essential for survival and growth.",
    dataChallenges: "Common data challenges organizations face:",
    
    scatteredData: "Scattered data across multiple systems and spreadsheets",
    inconsistentAnalysis: "Inconsistent analysis and reporting methodologies",
    limitedVisibility: "Limited visibility into key metrics and performance indicators",
    timelyChallenges: "Difficulties getting timely access to relevant information",
    skillGaps: "Technical skill gaps within the team",
    
    platformPromise: "Our promise:",
    
    unifiedView: "Create a unified view of your business data",
    establishSingle: "Establish a single source of truth for reporting",
    automateCollection: "Automate data collection and processing",
    enableSelf: "Enable self-service access to insights",
    scalableFoundation: "Build a scalable foundation for advanced analytics",
    
    structuredApproach: "Our Structured Approach",
    provenMethodology: "A proven methodology for building your data platform",
    approachSteps: "Our approach creates alignment between business needs and technical implementation, ensuring your data platform delivers measurable value.",
    
    ourProcess: "Our Process",
    comprehensiveProcess: "Comprehensive, end-to-end implementation",
    
    discovery: "1. Discovery & Requirements",
    discoveryDesc: "We identify key use cases, assess data sources, and define success metrics together with stakeholders.",
    
    design: "2. Solution Design",
    designDesc: "We architect the data platform, select appropriate technologies, and create a roadmap for implementation.",
    
    dataImplementationStep: "3. Implementation",
    implementationDescText: "We build data pipelines, integrate data sources, and develop the data warehouse structure.",
    
    visualization: "4. Visualization & Reporting",
    visualizationDesc: "We create dashboards and reports that make the data accessible and actionable for users.",
    
    adoption: "5. Adoption & Training",
    adoptionDesc: "We ensure users understand how to use the platform and extract maximum value from it.",
    
    evolution: "6. Evolution & Optimization",
    evolutionDesc: "We continuously improve the platform based on feedback and changing business needs.",
  },
  de: {
    // Data Platform Page
    dataPlatform: "Maßgeschneiderte Unternehmens-Datenplattform",
    buildFoundation: "Aufbau der Grundlage für datengesteuerten Erfolg",
    platformDesc: "Wir helfen Organisationen bei der Navigation durch den Übergang zu datengesteuerten Operationen durch eine strukturierte, bewährte Methodik.",
    
    challengeOpportunity: "Die Herausforderung und Chance",
    businessContext: "In der heutigen Geschäftslandschaft ist datengestützte Entscheidungsfindung keine Option mehr - sie ist wesentlich für das Überleben und Wachstum.",
    dataChallenges: "Häufige Datenherausforderungen, mit denen Organisationen konfrontiert sind:",
    
    scatteredData: "Verteilte Daten über mehrere Systeme und Tabellenkalkulationen",
    inconsistentAnalysis: "Inkonsistente Analyse- und Berichtsmethoden",
    limitedVisibility: "Begrenzte Sichtbarkeit auf Schlüsselmetriken und Leistungsindikatoren",
    timelyChallenges: "Schwierigkeiten beim rechtzeitigen Zugriff auf relevante Informationen",
    skillGaps: "Technische Qualifikationslücken innerhalb des Teams",
    
    platformPromise: "Unser Versprechen:",
    
    unifiedView: "Erstellen einer einheitlichen Ansicht Ihrer Geschäftsdaten",
    establishSingle: "Etablieren einer einzigen Wahrheitsquelle für die Berichterstattung",
    automateCollection: "Automatisieren der Datenerfassung und -verarbeitung",
    enableSelf: "Ermöglichen von Self-Service-Zugriff auf Erkenntnisse",
    scalableFoundation: "Aufbau einer skalierbaren Grundlage für fortschrittliche Analysen",
    
    structuredApproach: "Unser strukturierter Ansatz",
    provenMethodology: "Eine bewährte Methodik für den Aufbau Ihrer Datenplattform",
    approachSteps: "Unser Ansatz schafft eine Ausrichtung zwischen Geschäftsanforderungen und technischer Implementierung und stellt sicher, dass Ihre Datenplattform messbaren Wert liefert.",
    
    ourProcess: "Unser Prozess",
    comprehensiveProcess: "Umfassende, End-to-End-Implementierung",
    
    discovery: "1. Entdeckung & Anforderungen",
    discoveryDesc: "Wir identifizieren Schlüsselanwendungsfälle, bewerten Datenquellen und definieren Erfolgskennzahlen gemeinsam mit Stakeholdern.",
    
    design: "2. Lösungsdesign",
    designDesc: "Wir entwerfen die Datenplattform, wählen geeignete Technologien und erstellen einen Fahrplan für die Implementierung.",
    
    dataImplementationStep: "3. Implementierung",
    implementationDescText: "Wir bauen Datenpipelines, integrieren Datenquellen und entwickeln die Struktur des Data Warehouse.",
    
    visualization: "4. Visualisierung & Berichterstattung",
    visualizationDesc: "Wir erstellen Dashboards und Berichte, die die Daten für Benutzer zugänglich und umsetzbar machen.",
    
    adoption: "5. Adoption & Schulung",
    adoptionDesc: "Wir stellen sicher, dass Benutzer verstehen, wie sie die Plattform nutzen und maximalen Wert daraus ziehen können.",
    
    evolution: "6. Evolution & Optimierung",
    evolutionDesc: "Wir verbessern die Plattform kontinuierlich basierend auf Feedback und sich ändernden Geschäftsanforderungen.",
  },
  sk: {
    // Data Platform Page - using English as fallback for now
    dataPlatform: "Prispôsobená organizačná dátová platforma",
    buildFoundation: "Budovanie základov pre úspech založený na dátach",
    platformDesc: "Pomáhame organizáciám prechádzať k dátovo riadeným operáciám prostredníctvom štruktúrovanej, osvedčenej metodológie.",
    
    challengeOpportunity: "The Challenge and Opportunity",
    businessContext: "In today's business landscape, data-driven decision making is no longer optional—it's essential for survival and growth.",
    dataChallenges: "Common data challenges organizations face:",
    
    scatteredData: "Scattered data across multiple systems and spreadsheets",
    inconsistentAnalysis: "Inconsistent analysis and reporting methodologies",
    limitedVisibility: "Limited visibility into key metrics and performance indicators",
    timelyChallenges: "Difficulties getting timely access to relevant information",
    skillGaps: "Technical skill gaps within the team",
    
    platformPromise: "Our promise:",
    
    unifiedView: "Create a unified view of your business data",
    establishSingle: "Establish a single source of truth for reporting",
    automateCollection: "Automate data collection and processing",
    enableSelf: "Enable self-service access to insights",
    scalableFoundation: "Build a scalable foundation for advanced analytics",
    
    structuredApproach: "Our Structured Approach",
    provenMethodology: "A proven methodology for building your data platform",
    approachSteps: "Our approach creates alignment between business needs and technical implementation, ensuring your data platform delivers measurable value.",
    
    ourProcess: "Our Process",
    comprehensiveProcess: "Comprehensive, end-to-end implementation",
    
    discovery: "1. Discovery & Requirements",
    discoveryDesc: "We identify key use cases, assess data sources, and define success metrics together with stakeholders.",
    
    design: "2. Solution Design",
    designDesc: "We architect the data platform, select appropriate technologies, and create a roadmap for implementation.",
    
    dataImplementationStep: "3. Implementation",
    implementationDescText: "We build data pipelines, integrate data sources, and develop the data warehouse structure.",
    
    visualization: "4. Visualization & Reporting",
    visualizationDesc: "We create dashboards and reports that make the data accessible and actionable for users.",
    
    adoption: "5. Adoption & Training",
    adoptionDesc: "We ensure users understand how to use the platform and extract maximum value from it.",
    
    evolution: "6. Evolution & Optimization",
    evolutionDesc: "We continuously improve the platform based on feedback and changing business needs.",
  },
  cs: {
    // Data Platform Page
    dataPlatform: "Přizpůsobená organizační datová platforma",
    buildFoundation: "Budování základů pro úspěch založený na datech",
    platformDesc: "Pomáháme organizacím přecházet k datově řízeným operacím prostřednictvím strukturované, osvědčené metodologie.",
    
    challengeOpportunity: "Výzva a příležitost",
    businessContext: "V dnešní obchodní krajině už není rozhodování založené na datech volitelné - je nezbytné pro přežití a růst.",
    dataChallenges: "Běžné datové výzvy, se kterými se organizace potýkají:",
    
    scatteredData: "Rozptýlená data napříč více systémy a tabulkami",
    inconsistentAnalysis: "Nekonzistentní analytické a reportovací metodologie",
    limitedVisibility: "Omezená viditelnost klíčových metrik a ukazatelů výkonnosti",
    timelyChallenges: "Potíže s včasným přístupem k relevantním informacím",
    skillGaps: "Technické dovednostní mezery v rámci týmu",
    
    platformPromise: "Náš slib:",
    
    unifiedView: "Vytvořte jednotný pohled na vaše obchodní data",
    establishSingle: "Vytvořte jediný zdroj pravdy pro reportování",
    automateCollection: "Automatizujte sběr a zpracování dat",
    enableSelf: "Umožněte samoobslužný přístup k poznatkům",
    scalableFoundation: "Vybudujte škálovatelný základ pro pokročilou analýzu",
    
    structuredApproach: "Náš strukturovaný přístup",
    provenMethodology: "Osvědčená metodologie pro vybudování vaší datové platformy",
    approachSteps: "Náš přístup vytváří soulad mezi obchodními potřebami a technickou implementací a zajišťuje, že vaše datová platforma přináší měřitelnou hodnotu.",
    
    ourProcess: "Náš proces",
    comprehensiveProcess: "Komplexní, end-to-end implementace",
    
    discovery: "1. Objevování a požadavky",
    discoveryDesc: "Společně se zainteresovanými stranami identifikujeme klíčové případy užití, posuzujeme zdroje dat a definujeme metriky úspěchu.",
    
    design: "2. Návrh řešení",
    designDesc: "Navrhneme datovou platformu, vybereme vhodné technologie a vytvoříme plán implementace.",
    
    dataImplementationStep: "3. Implementace",
    implementationDescText: "Budujeme datové pipeline, integrujeme zdroje dat a vyvíjíme strukturu datového skladu.",
    
    visualization: "4. Vizualizace a reportování",
    visualizationDesc: "Vytváříme dashbordy a reporty, které činí data dostupná a použitelná pro uživatele.",
    
    adoption: "5. Adopce a školení",
    adoptionDesc: "Zajišťujeme, aby uživatelé rozuměli, jak platformu používat a získat z ní maximální hodnotu.",
    
    evolution: "6. Evoluce a optimalizace",
    evolutionDesc: "Neustále vylepšujeme platformu na základě zpětné vazby a měnících se obchodních potřeb.",
  }
};
